/** Live streaming text with blinking cursor — throttled to prevent flicker.
 *  Buffers partial code fences so raw unfenced code doesn't render as plain text. */
import { useState, useEffect, useRef } from "react";
import { DevAgentChatMessageContent } from "../chat/DevAgentChatMessageContent";
import { normalizeAssistantContent } from "../normalizeAssistantContent";

/** Trim content to the last complete code fence boundary.
 *  If there's an unclosed ``` block, cut before it — Streamdown
 *  can't render partial fences properly and shows raw code. */
function trimToCompleteFence(text: string): string {
  text = normalizeAssistantContent(text);
  const fencePattern = /```/g;
  let count = 0;
  let lastOpenIdx = -1;
  let match;
  while ((match = fencePattern.exec(text)) !== null) {
    count++;
    if (count % 2 === 1) lastOpenIdx = match.index;
  }
  // Odd count = unclosed fence — trim to before it
  if (count % 2 === 1 && lastOpenIdx >= 0) {
    return text.slice(0, lastOpenIdx);
  }
  return text;
}

export function StreamingText({ content }: { content: string }) {
  const [rendered, setRendered] = useState(content);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef(content);
  latestRef.current = content;

  useEffect(() => {
    if (!timerRef.current) {
      timerRef.current = setTimeout(() => {
        setRendered(trimToCompleteFence(latestRef.current));
        timerRef.current = null;
      }, 150);
    }
    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [content]);

  useEffect(() => {
    return () => setRendered(latestRef.current);
  }, []);

  if (!content) return null;
  return (
    <div className="py-1.5">
      <div className="px-4 py-2.5 max-w-full text-sm select-text">
        <DevAgentChatMessageContent content={rendered} isStreaming={true} />
        <span className="inline-block w-2 h-4 ml-0.5 bg-foreground/60 animate-pulse rounded-sm" />
      </div>
    </div>
  );
}
